import { PricingPeriod } from '@atlassiansox/bxp-use-pricing';
import React, { ReactNode } from 'react';
import { FormattedMessage } from 'react-intl';
import {
  Annual,
  CenterJustifiedContainer,
  EnterpriseAnnualBillingMessage,
  EnterpriseContact,
  EnterpriseNoJPY,
  EnterpriseUnmetMin,
  Free,
  Monthly,
  SpecialPlanNames,
  SpecialProductKeys
} from '../..';
import { usePriceFinderStore } from '../../store';

type PlanCardBodyProps = {
  billingCycle: PricingPeriod;
  fontSize: string;
  overSignupLimit: boolean;
  planName: string;
  currencyISOCode: string;
  unitCount: number;
  unitMinEnterprise: number;
  productKey: string;
  priceCalcIdx: number;
  contactURL: string;
};

const PlanCardBody = ({
  billingCycle,
  fontSize,
  overSignupLimit,
  planName,
  currencyISOCode,
  unitCount,
  unitMinEnterprise,
  productKey,
  priceCalcIdx,
  contactURL
}: PlanCardBodyProps) => {
  const [{ priceCalcs }, actions] = usePriceFinderStore();
  const priceCalc = priceCalcs[priceCalcIdx];

  if (!priceCalc) {
    throw new Error(
      `Price calculation was not found for index ${priceCalcIdx}`
    );
  }

  const { formattedTotalPrice } = priceCalc;

  let planMessage =
    billingCycle === 'MONTHLY' ? (
      <Monthly priceCalc={priceCalc} planName={planName} fontSize={fontSize} />
    ) : (
      <Annual priceCalc={priceCalc} planName={planName} fontSize={fontSize} />
    );

  if (overSignupLimit && productKey === SpecialProductKeys.BITBUCKET) {
    planMessage = (
      <CenterJustifiedContainer>
        <FormattedMessage
          tagName={'span'}
          id="bitbucketOverMaxUserLimit"
          defaultMessage="For teams of more than 35,000, <a>contact our sales team.</a>"
          values={{
            a: (chunks: ReactNode) => <a href={contactURL}>{chunks}</a>
          }}
        />
      </CenterJustifiedContainer>
    );
  } else if (overSignupLimit) {
    planMessage = (
      <CenterJustifiedContainer>
        <FormattedMessage
          defaultMessage="Your user count exceeds this plan’s limit"
          description="Over max unit count"
          id="overMaxUnitCount"
        />
      </CenterJustifiedContainer>
    );
  }

  switch (planName) {
    case SpecialPlanNames.FREE:
      planMessage = (
        <Free
          planName={planName}
          formattedZeroPrice={formattedTotalPrice}
          fontSize={fontSize}
        />
      );
      break;

    case SpecialPlanNames.ENTERPRISE:
      if (billingCycle === 'MONTHLY') {
        planMessage = (
          <EnterpriseAnnualBillingMessage
            onToggleToAnnualBilling={() => actions.updateBillingCycle('ANNUAL')}
          />
        );
      } else if (currencyISOCode === 'JPY') {
        planMessage = <EnterpriseNoJPY />;
      } else if (overSignupLimit) {
        planMessage = <EnterpriseContact />;
      } else if (unitCount < unitMinEnterprise) {
        planMessage = (
          <EnterpriseUnmetMin minRequiredUnits={unitMinEnterprise} />
        );
      }
      break;
  }
  return planMessage;
};

export { PlanCardBody };
